import { EmailOutlined } from '@mui/icons-material';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FgaUserState } from '@/generated/model';

import type { UserAccessState } from '../../types/UserAccessState';
import { getStateDescriptionKey, getStateLabelKey } from '../../utils/userStateUtils';

type UserDetailsStateAlertProps = {
  state: UserAccessState;
  canResendInvitation: boolean;
  isResendingInvitation: boolean;
  onResendInvitation: () => void;
};

export function UserDetailsStateAlert({
  state,
  canResendInvitation,
  isResendingInvitation,
  onResendInvitation,
}: UserDetailsStateAlertProps) {
  const { t } = useTranslation();

  if (state === FgaUserState.WAITING_FOR_ACTIVATION) {
    return (
      <Stack spacing={0.5}>
        <Typography variant="caption" color="error.main">
          {t('userAccessControl.states.waitingForActivation')}
        </Typography>
        {canResendInvitation && (
          <Button
            fullWidth
            variant="contained"
            color="error"
            startIcon={isResendingInvitation ? <CircularProgress size={18} color="inherit" /> : <EmailOutlined />}
            disabled={isResendingInvitation}
            onClick={onResendInvitation}
          >
            {t('userAccessControl.details.resendInvitation')}
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Alert severity={state === FgaUserState.ACTIVE ? 'success' : 'error'}>
      <Typography variant="subtitle2">{t(getStateLabelKey(state))}</Typography>
      <Typography variant="body2">{t(getStateDescriptionKey(state))}</Typography>
    </Alert>
  );
}
